import { Injectable } from '@angular/core';
import {AuditDto, EntryDto, EntryDtoLight, FormDto, FormDtoLight, FormValidator} from "../../api";
import {ValidatorFn, Validators} from "@angular/forms";
import {TimeValidator} from "../../validators/timeValidator";
import {DateValidator} from "../../validators/dateValidator";

@Injectable({
  providedIn: 'root'
})
export class FormParserService {

  constructor() { }

  getTitle(obj: AuditDto | EntryDto): string {
    if (this.isAudit(obj)) {
      return this.getAuditTitle(obj);
    }

    return this.getEntryTitle(obj);
  }

  getSubtitle(obj: AuditDto | EntryDto): string {
    if (this.isAudit(obj)) {
      return this.getAuditSubtitle(obj);
    }

    return this.getEntrySubtitle(obj);
  }

  hasConfiguredSubtitle(entry: EntryDto | EntryDtoLight): boolean {
    const form = this.getFormFromEntry(entry);
    if (!form?.metaData) return false;

    if (entry.levelId && form.metaData.children) {
      const level = this.findLevelById(form.metaData.children as any[], entry.levelId);
      if (level?.subtitleFieldId) return true;
      if (level?.uiDescriptor?.listItem?.subTitleTemplate) return true;
    }

    if ((form.metaData as any)?.subtitleFieldId) return true;
    if (form.metaData?.uiDescriptor?.listItem?.subTitleTemplate) return true;

    return false;
  }

  getFormName(form: FormDto | FormDtoLight | undefined): string {
    if (!form?.metaData) return '';
    const uiDescriptor = form.metaData.uiDescriptor as any;
    const titleTemplate: string | undefined = uiDescriptor?.listItem?.titleTemplate;
    if (titleTemplate && !titleTemplate.includes('#{')) return titleTemplate;
    return uiDescriptor?.formTitle ?? '';
  }

  getLevelName(entry: EntryDto | EntryDtoLight): string {
    const form = this.getFormFromEntry(entry);

    if (entry.levelId && form?.metaData?.children) {
      const level = this.findLevelById(form.metaData.children as any[], entry.levelId);
      if (level?.name) return level.name;
    }

    return form?.metaData?.uiDescriptor?.formTitle ?? '';
  }

  getChildLevelFields(form: FormDto | FormDtoLight | undefined, levelId: string): any[] {
    if (!form?.metaData?.children) return [];
    const level = this.findLevelById(form.metaData.children as any[], levelId);
    return level?.fields ?? [];
  }

  getChildLevels(form: FormDto | FormDtoLight | undefined): any[] {
    if (!form?.metaData?.children) return [];
    return form.metaData.children as any[];
  }

  /** Child level definitions nested under a specific level (DOCU-653). */
  getChildLevelsOf(form: FormDto | FormDtoLight | undefined, levelId: string): any[] {
    if (!form?.metaData?.children) return [];
    const level = this.findLevelById(form.metaData.children as any[], levelId);
    return level?.children ?? [];
  }

  getFormTitle(form: FormDto | undefined): string {
    if (form == undefined) return '';
    return form.metaData?.uiDescriptor?.formTitle ?? '';
  }

  private getAuditTitle(audit: AuditDto): string {
    // DOCU-770: prefer the persisted _title — it is what the App shows, so BO and
    // App display the same value and a stale/broken _title is visible instead of
    // being masked by live rendering. Live template rendering stays as fallback
    // for legacy audits created before _title/_subtitle were persisted (DOCU-612).
    const stored = this.storedMetaValue(audit, '_title');
    if (stored) return stored;

    const ownTitle = this.resolveTitleFromFormAndMeta(audit.form, audit.metaData, undefined);
    if (ownTitle) return ownTitle;

    const rootEntry = this.findRootEntry(audit);
    if (rootEntry?.metaData) {
      const rootTitle = this.resolveTitleFromFormAndMeta(audit.form, rootEntry.metaData, undefined);
      if (rootTitle) return rootTitle;
    }

    return '';
  }

  private getAuditSubtitle(audit: AuditDto): string {
    // DOCU-770: same stored-first strategy as getAuditTitle.
    const stored = this.storedMetaValue(audit, '_subtitle');
    if (stored) return stored;

    const ownSubtitle = this.resolveSubtitleFromFormAndMeta(audit.form, audit.metaData, undefined);
    if (ownSubtitle) return ownSubtitle;

    const rootEntry = this.findRootEntry(audit);
    if (rootEntry?.metaData) {
      const rootSubtitle = this.resolveSubtitleFromFormAndMeta(audit.form, rootEntry.metaData, undefined);
      if (rootSubtitle) return rootSubtitle;
    }

    return '';
  }

  private storedMetaValue(audit: AuditDto, key: '_title' | '_subtitle'): string {
    const value = audit.metaData?.[key];
    return value == null ? '' : String(value).trim();
  }

  private findRootEntry(audit: AuditDto) {
    return (audit.entries ?? []).find(e => !e?.deleted && !e?.levelId);
  }

  private getEntryTitle(entry: EntryDto): string {
    const form = this.getFormFromEntry(entry);
    return this.resolveTitleFromFormAndMeta(form, entry.metaData, (entry as any).levelId);
  }

  private getEntrySubtitle(entry: EntryDto): string {
    const form = this.getFormFromEntry(entry);
    return this.resolveSubtitleFromFormAndMeta(form, entry.metaData, (entry as any).levelId);
  }

  private resolveTitleFromFormAndMeta(
    form: FormDto | FormDtoLight | undefined,
    metaData: { [key: string]: any } | undefined,
    levelId?: string
  ): string {

    if (!form?.metaData || !metaData) return '';

    if (levelId && form.metaData.children) {

      const level = this.findLevelById(form.metaData.children as any[], levelId);

      // 1. Priorität: titleFieldId
      if (level?.titleFieldId) {
        const fieldId = this.parseTemplateFieldId(level.titleFieldId);
        if (fieldId) {
          const value = metaData[fieldId];
          if (this.hasValue(value)) return String(value);
        }
      }

      // 2. Priorität: Level Template
      const levelTemplate = level?.uiDescriptor?.listItem?.titleTemplate;
      const resolvedLevelTemplate = this.resolveTemplate(levelTemplate, metaData);
      if (resolvedLevelTemplate) return resolvedLevelTemplate;
    }

    // 3. Formular Template
    const template = form.metaData?.uiDescriptor?.listItem?.titleTemplate;
    const resolvedTemplate = this.resolveTemplate(template, metaData);
    if (resolvedTemplate) return resolvedTemplate;

    // 4. rootTitleFieldId
    const rootTitleFieldId = (form.metaData as any)?.titleFieldId;
    if (rootTitleFieldId) {
      const fieldId = this.parseTemplateFieldId(rootTitleFieldId);
      if (fieldId) {
        const value = metaData[fieldId];
        if (this.hasValue(value)) return String(value);
      }
    }

    return '';
  }

  private resolveSubtitleFromFormAndMeta(
    form: FormDto | FormDtoLight | undefined,
    metaData: { [key: string]: any } | undefined,
    levelId?: string
  ): string {

    if (!form?.metaData || !metaData) return '';

    if (levelId && form.metaData.children) {

      const level = this.findLevelById(form.metaData.children as any[], levelId);

      // 1. subtitleFieldId
      if (level?.subtitleFieldId) {
        const fieldId = this.parseTemplateFieldId(level.subtitleFieldId);
        if (fieldId) {
          const value = metaData[fieldId];
          if (this.hasValue(value)) return String(value);
        }
      }

      // 2. Level Template
      const levelTemplate = level?.uiDescriptor?.listItem?.subTitleTemplate;
      const resolvedLevelTemplate = this.resolveTemplate(levelTemplate, metaData);
      if (resolvedLevelTemplate) return resolvedLevelTemplate;
    }

    // 3. root subtitle
    const rootSubtitleFieldId = (form.metaData as any)?.subtitleFieldId;
    if (rootSubtitleFieldId) {
      const fieldId = this.parseTemplateFieldId(rootSubtitleFieldId);
      if (fieldId) {
        const value = metaData[fieldId];
        if (this.hasValue(value)) return String(value);
      }
    }

    // 4. form template
    const template = form.metaData?.uiDescriptor?.listItem?.subTitleTemplate;
    const resolvedTemplate = this.resolveTemplate(template, metaData);
    if (resolvedTemplate) return resolvedTemplate;

    return '';
  }

  private resolveTemplate(template: string | undefined, metaData: { [key: string]: any }): string {
    if (!template) return '';

    if (!template.includes('#{')) {
      return template.trim();
    }

    let resolved = template;
    Object.entries(metaData).forEach(([key, value]) => {
      resolved = resolved.replaceAll(`#{${key}}`, value == null ? '' : String(value));
    });

    return resolved.includes('#{') ? '' : resolved.trim();
  }

  private getFormFromEntry(entry: EntryDto | EntryDtoLight): FormDto | FormDtoLight | undefined {
    return entry.form ?? (entry as any).audit?.form;
  }

  private hasValue(value: any): boolean {
    return value !== undefined && value !== null && value !== '';
  }

  private isAudit(obj: AuditDto | EntryDto): obj is AuditDto {
    return !('levelId' in (obj as any)) && !!((obj as any).entries || (obj as any).status !== undefined || (obj as any).userId);
  }

  private findLevelById(children: any[], levelId: string): any {
    for (const child of children) {
      if (child.id === levelId) return child;
      if (child.children?.length) {
        const found = this.findLevelById(child.children, levelId);
        if (found) return found;
      }
    }
    return null;
  }

  private parseTemplateFieldId(value?: string): string | null {
    if (!value) return null;
    if (value.startsWith('#{') && value.endsWith('}')) return value.slice(2, -1);
    return value;
  }

  /**
   * DOCU-869: Liest einen Zahlwert der Validator-Options unter allen Schreibweisen,
   * die in gespeicherten Templates vorkommen — dem Sollformat, dem PascalCase der
   * API-Altbestände und dem `value` des Formularbuilders. Liefert `undefined`, wenn
   * keine davon einen brauchbaren Wert trägt.
   *
   * Die Unterscheidung zwischen `undefined` und `0` ist wesentlich: `0` ist eine
   * gültige Untergrenze („nicht negativ"), ein fehlender Wert dagegen darf keine
   * Grenze erzeugen.
   */
  private readOptionNumber(options: unknown, ...keys: string[]): number | undefined {
    if (!options) return undefined;
    const bag = options as Record<string, unknown>;

    for (const key of keys) {
      const raw = bag[key];
      if (raw === undefined || raw === null) continue;

      const num = typeof raw === 'number' ? raw : Number(raw);
      if (!Number.isNaN(num)) return num;
    }

    return undefined;
  }

  buildValidators(configs: FormValidator[]): ValidatorFn[] {
    if (!configs) return [];

    const validators: ValidatorFn[] = [];

    for (const conf of configs) {
      switch (conf.type) {
        case 'required':
          validators.push(Validators.required);
          break;
        // DOCU-1104: Eine Länge von 0 (oder weniger) heißt „keine Grenze" — so liest es
        // die App (nur > 0 wird geprüft). 71 aktive Prod-Vorlagen tragen
        // `maxLength: {MaxLength: 0}`; wörtlich genommen machte das jede Texteingabe
        // ungültig und blockierte das Speichern. Anders als bei min/max unten ist 0 hier
        // keine sinnvolle Grenze.
        case 'minLength': {
          const minLength = this.readOptionNumber(conf.options, 'minLength', 'MinLength', 'value');
          if (minLength !== undefined && minLength > 0) validators.push(Validators.minLength(minLength));
          break;
        }
        case 'maxLength': {
          const maxLength = this.readOptionNumber(conf.options, 'maxLength', 'MaxLength', 'value');
          if (maxLength !== undefined && maxLength > 0) validators.push(Validators.maxLength(maxLength));
          break;
        }
        // DOCU-869: Zahlengrenzen wurden hier nie durchgesetzt. Das fiel nicht auf,
        // solange die API sie ohnehin verwarf — seit sie erhalten bleiben, prüfte die
        // App sie und das Erfassungsformular nicht, was denselben Datensatz je nach
        // Erfassungsweg zulässig oder unzulässig machte.
        case 'min': {
          const min = this.readOptionNumber(conf.options, 'value', 'Value', 'min');
          // Nur bei tatsächlich vorhandener Grenze: Auf Prod stehen 14 min/max-Paare
          // ganz ohne Options. Ein Fallback auf 0 machte daraus stillschweigend ein
          // Verbot negativer Zahlen.
          if (min !== undefined) validators.push(Validators.min(min));
          break;
        }
        case 'max': {
          const max = this.readOptionNumber(conf.options, 'value', 'Value', 'max');
          if (max !== undefined) validators.push(Validators.max(max));
          break;
        }
        case 'dateText':
          // @ts-ignore
          // validators.push(DateValidator);
          break;
        case 'timeText':
          // @ts-ignore
          validators.push(TimeValidator);
          break;
      }
    }

    return validators;
  }
}
